/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TemplatesApi } from '../apis';
import { TemplateContext } from '../contexts';
import { IFullTemplate } from '../../config/interfaces';

/**
 * Redux slice for managing template-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to survey templates, including:
 * - Fetching templates by ID
 * - Managing questions within templates including resetting questions
 * - Managing loading, error, and success states for asynchronous template operations
 *
 * @property {object} initialState - The initial state for the template context.
 * @property {Function} getTemplateByIdSuccess - Handles successful fetch of a template by ID.
 * @property {Function} getTemplateByIdFailure - Handles failed fetch of a template by ID.
 * @property {Function} clearTemplateById - Clears the template fetched by ID from the state.
 * @property {Function} editSelectedQuestion - Sets the currently selected question for editing.
 * @property {Function} resetEditQuestion - Resets the edit state for questions.
 *
 * @example
 * dispatch(editSelectedQuestion(questionId));
 *
 * @returns {object} The template slice reducer and actions.
 */
const templateSlice = createSlice({
  name: 'template',
  initialState: TemplateContext,
  reducers: {
    getTemplateByIdSuccess: (state, action: PayloadAction<IFullTemplate>) => {
      return {
        ...state,
        templateById: action.payload
      };
    },
    getTemplateByIdFailure: state => {
      return {
        ...state,
        templateById: {} as IFullTemplate
      };
    },
    clearTemplateById: state => {
      return {
        ...state,
        templateById: {} as IFullTemplate
      };
    },
    editSelectedQuestion: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        editQuestion: true,
        selectedQuestionId: action.payload
      };
    },
    resetEditQuestion: state => {
      return {
        ...state,
        editQuestion: false,
        selectedQuestionId: ''
      };
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      TemplatesApi.endpoints.getFullRoundSurvey.matchFulfilled,
      (state, action) => ({
        ...state,
        prevTemplate:
          action.payload.roundingType === 'patient'
            ? action.payload.templateConfigId
            : ''
      })
    );
  }
});

export const {
  getTemplateByIdSuccess,
  getTemplateByIdFailure,
  clearTemplateById,
  editSelectedQuestion,
  resetEditQuestion
} = templateSlice.actions;

export default templateSlice.reducer;
